// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';

// Services
import { CoreService } from '../../../../shared/services/core.service';
import { ReelService } from '../services/reel.service';
import { SpinnerService } from '../../../../shared/components/spinner/services/spinner.service';
import { DialogService } from '../../../../shared/services/dialog.service';

// Models
import { ReelViewModel } from '../models/reel-view.model';
import { AppCleanup } from '../../../../shared/models';
import { ConfirmDialogModel } from '../../../../shared/dialogs/confirm-dialog/confirm-dialog.component';

// Store
import { ReelActions } from './reel.action';

// Enums
import { StatusType } from '../../../../shared/enums';

// Helpers
import { UtilityHelper } from '../../../../shared/helpers';

export interface ReelStateModel {
	items: ReelViewModel[];
	loaded: boolean;
}

const defaultState: ReelStateModel = {
	items: undefined,
	loaded: false,
};

@State<any>({
	name: 'reel',
	defaults: defaultState,
})
@Injectable()
export class ReelState extends AppCleanup {
	constructor(
		private reelService: ReelService,
		private spinnerService: SpinnerService,
		private coreService: CoreService,
		private translateService: TranslateService,
		private dialogService: DialogService
	) {
		super();
	}

	@Action(ReelActions.GetAll)
	getAll(ctx: StateContext<ReelStateModel>, action: ReelActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.reelService
			.getReels()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (reels) => {
					ctx.setState({
						items: reels,
						loaded: true,
					});

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.REEL.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(ReelActions.SetAll)
	setAll(ctx: StateContext<ReelStateModel>, action: ReelActions.SetAll) {
		ctx.setState({
			items: action.reels,
			loaded: true,
		});
	}

	@Action(ReelActions.Create)
	create(ctx: StateContext<ReelStateModel>, action: ReelActions.Create) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.reelService
			.createReel(action.data.reel)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (reel) => {
					ctx.setState(
						patch({
							items: append([reel]),
						})
					);

					ctx.dispatch(new ReelActions.CreateSuccess(reel));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.CREATED', { name: this.translateService.instant('MODULE.REEL.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new ReelActions.CreateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_CREATED', { name: this.translateService.instant('MODULE.REEL.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(ReelActions.Update)
	update(ctx: StateContext<ReelStateModel>, action: ReelActions.Update) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.reelService
			.updateReel(action.data.reel)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (reel) => {
					const currentState = ctx.getState();
					const existingItem = currentState.items?.find((item) => item.id === reel.id);

					if (existingItem) {
						ctx.setState(
							patch({
								items: updateItem((reel) => reel.id === action.data.reel.id, action.data.reel),
							})
						);
					}

					ctx.dispatch(new ReelActions.UpdateSuccess(reel));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.REEL.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new ReelActions.UpdateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.REEL.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(ReelActions.Delete)
	delete(ctx: StateContext<ReelStateModel>, action: ReelActions.Delete) {
		let deleteReel = () => {
			if (action.data.showSpinner) {
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner
			}

			return this.reelService
				.deleteReel(action.data.id)
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: () => {
						const currentState = ctx.getState();
						const existingItem = currentState.items?.find((item) => item.id === action.data.id);

						if (existingItem) {
							ctx.setState(
								patch({
									items: removeItem((reel) => reel.id === action.data.id),
								})
							);
						}

						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.DELETED', { name: this.translateService.instant('MODULE.REEL.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						}
					},
					error: () => {
						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.NOT_DELETED', { name: this.translateService.instant('MODULE.REEL.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						}
					},
				});
		};

		if (action.data.showConfirm) {
			const confirmModel: ConfirmDialogModel = {
				title: `${UtilityHelper.capitalizeFirstLetter(this.translateService.instant('ACTION.DELETE'))} ${this.translateService.instant('MODULE.REEL.TITLE')}`,
				message: `${this.translateService.instant('GENERIC.CONFIRM_DELETE', { name: this.translateService.instant('MODULE.REEL.TITLE') })}`,
				action: this.translateService.instant('ACTION.DELETE_CAPITAL'),
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result) => {
					if (result) {
						deleteReel();
					}
				});
		} else {
			deleteReel();
		}
	}

	@Selector()
	static isLoaded(state: ReelStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static reels(state: ReelStateModel): ReelViewModel[] {
		return state.items;
	}
}
